import { useCallback } from 'react';
import { BaseAnalyticsCard } from './BaseAnalyticsCard';
import { BaseBarChart } from './BaseBarChart';
import { BaseTable, tableUtils } from './BaseTable';
import { TrendingUp } from 'lucide-react';
import { DetailModalProps, ItemModalProps, AnalyticsQueryResult } from './types';
import { DATA_LIMITS, ANALYTICS_CONFIG, CHART_CONFIG } from '../config';

interface BaseRankingCardProps<TData, TFilters> {
  title: string;
  filters: TFilters;
  dataHook: (filters: TFilters, limit?: number) => AnalyticsQueryResult<TData[]>;

  getTitle: (item: TData) => string;
  getMainMetric: (item: TData) => number;
  getSecondaryMetric?: (item: TData) => number;
  getPercentage?: (item: TData) => number;

  maxItems?: number;
  height?: number;
  titleMaxLength?: number;
  mainMetricLabel?: string;
  secondaryMetricLabel?: string;
  emptyMessage?: string;

  DetailModal?: React.ComponentType<DetailModalProps<TData, TFilters>>;
  ItemModal?: React.ComponentType<ItemModalProps<TData, TFilters>>;
}

export function BaseRankingCard<TData, TFilters>({
  title,
  filters,
  dataHook,
  getTitle,
  getMainMetric,
  getSecondaryMetric,
  getPercentage,
  maxItems = DATA_LIMITS.DEFAULT_ITEMS,
  height = 380,
  titleMaxLength = DATA_LIMITS.TITLE_MAX_LENGTH,
  mainMetricLabel = ANALYTICS_CONFIG.METRIC_LABELS.SESSIONS,
  secondaryMetricLabel = ANALYTICS_CONFIG.METRIC_LABELS.MINUTES,
  emptyMessage = 'No ranking data available',
  DetailModal,
  ItemModal,
}: BaseRankingCardProps<TData, TFilters>) {
  const renderChart = useCallback(
    (data: TData[], metric: string, onCardClick?: () => void) => {
      const chart = (
        <BaseBarChart
          data={data.slice(0, maxItems)}
          height={height}
          getTitle={getTitle}
          getValue={
            metric === ANALYTICS_CONFIG.METRIC_TYPES.SESSIONS
              ? getMainMetric
              : getSecondaryMetric || getMainMetric
          }
          titleMaxLength={titleMaxLength}
          metricLabel={
            metric === ANALYTICS_CONFIG.METRIC_TYPES.SESSIONS
              ? mainMetricLabel
              : secondaryMetricLabel
          }
        />
      );

      if (onCardClick) {
        return (
          <div onClick={onCardClick} className="cursor-pointer">
            {chart}
          </div>
        );
      }

      return chart;
    },
    [getTitle, getMainMetric, getSecondaryMetric, maxItems, height, titleMaxLength],
  );

  const renderTable = useCallback(
    (data: TData[], onItemClick?: (item: TData) => void) => {
      const columns = [
        tableUtils.titleWithRankColumn('title', 'Title', getTitle, {
          maxLength: DATA_LIMITS.TABLE_TITLE_MAX_LENGTH,
        }),
        tableUtils.metricColumn('main', mainMetricLabel, getMainMetric),
        ...(getSecondaryMetric
          ? [
              tableUtils.metricColumn('secondary', secondaryMetricLabel, getSecondaryMetric, {
                format: 'duration',
              }),
            ]
          : []),
        ...(getPercentage
          ? [
              tableUtils.metricWithIconColumn(
                'percentage',
                ANALYTICS_CONFIG.METRIC_LABELS.SHARE,
                getPercentage,
                <TrendingUp className="h-4 w-4" />,
                { format: 'percentage', iconColor: 'text-[var(--anl-success)]' },
              ),
            ]
          : []),
      ];

      return (
        <BaseTable
          data={data}
          columns={columns}
          maxRows={maxItems}
          onRowClick={onItemClick}
          keyExtractor={getTitle}
          emptyMessage={emptyMessage}
        />
      );
    },
    [
      getTitle,
      getMainMetric,
      getSecondaryMetric,
      getPercentage,
      maxItems,
      mainMetricLabel,
      secondaryMetricLabel,
      emptyMessage,
    ],
  );

  return (
    <BaseAnalyticsCard
      title={title}
      filters={filters}
      dataHook={(f: TFilters) => dataHook(f, DATA_LIMITS.MAX_CHART_ITEMS)}
      renderChart={renderChart}
      renderTable={renderTable}
      emptyMessage={emptyMessage}
      accent={CHART_CONFIG.COLORS.PRIMARY}
      DetailModal={DetailModal}
      ItemModal={ItemModal}
    />
  );
}
